import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { customerApi } from "../../../apis/customer/api";
import { drayageApi } from "../../../apis/drayage/api";
import {
  CalculateTollsQueryType,
  CalculateDrayageQuoteResponse,
  CalculateDrayageQuoteParams,
} from "../../../apis/drayage/types";
import {
  BaseCustomerProfileParams,
  CustomerProfile,
  DrayageContainerSizesEnum,
  DrayageLocation,
  DrayageQuote,
  DrayageSearchShippingLocations,
  DrayageShippingLocationsProperties,
  FuelSurcharge,
  LocationParams,
  OperationExpense,
  RouteSearchFieldMethods,
  TariffPricingTierLimited,
  TollBarriersData,
} from "../../../types/drayage";
import { Option } from "../../../types/common";
import { LoadingTypesEnum } from "../../../types/loading";
import { useNotification } from "../../../contextApis/NotificationContext";
import { useRequiredData } from "../../../contextApis/RequiredDataContext";
import {
  initialFSCParams,
  initialOEParams,
  initialTPTParams,
} from "./Search.data";
import { getApiErrorForDisplay } from "../../../utils/apiError";

/* global HTMLDivElement */

const initialCalculationData: CalculateDrayageQuoteParams = {
  tariffPricingTierId: 0,
  companyId: undefined,
  fuelSurchargeProfileId: 0,
  operationalExpensesProfileId: 0,
  containerSize: DrayageContainerSizesEnum.Size40ft,
  containerPickupLocation: {
    terminalGroupId: null,
    terminalId: null,
    depotId: null,
    locationGroupId: null,
    tariffPricingTierId: null,
    locationAddress: null,
  },
  containerDeliveryLocation: {
    terminalGroupId: null,
    terminalId: null,
    depotId: null,
    locationGroupId: null,
    tariffPricingTierId: null,
    locationAddress: null,
  },
  externalProvider: "Outlook",
};

const useDrayageCalculator = () => {
  const { addNotification } = useNotification();
  const { loading, loadTypes, companyDetails } = useRequiredData();

  const routeOrgSearchFieldRef = useRef<RouteSearchFieldMethods | null>(null);
  const routeDestSearchFieldRef = useRef<RouteSearchFieldMethods | null>(null);
  const leftContentRef = useRef<HTMLDivElement | null>(null);
  const routeCardsListRef = useRef<HTMLDivElement | null>(null);

  const defaultDrayageTariffPricingTier =
    loading === LoadingTypesEnum.fulfilled
      ? loadTypes?.loadTypeConfigurations?.defaultDrayageTariffPricingTier
      : undefined;

  const [calculationData, setCalculationData] =
    useState<CalculateDrayageQuoteParams>(initialCalculationData);
  const [isTerminalPickup, setIsTerminalPickup] = useState(true);

  const [profiles, setProfiles] = useState({
    customer: [] as CustomerProfile[],
    pricing: [] as TariffPricingTierLimited[],
    fsc: [] as FuelSurcharge[],
    oe: [] as OperationExpense[],
  });

  const [uiState, setUiState] = useState({
    loading: LoadingTypesEnum.idle as LoadingTypesEnum,
    newCustomerProfileLoading: LoadingTypesEnum.idle as LoadingTypesEnum,
    tollsLoading: LoadingTypesEnum.idle as LoadingTypesEnum,
    accordionOpenItems: [] as string[],
    isAddCustomerProfileFormOpen: false,
    isRouteDetailsCardOpen: false,
    isPrintModalOpen: false,
  });

  const [calculationState, setCalculationState] = useState({
    response: undefined as CalculateDrayageQuoteResponse | undefined,
    selectedQuote: undefined as DrayageQuote | undefined,
    allTolls: undefined as CalculateTollsQueryType["Data"] | undefined,
  });

  const [shippingLocations, setShippingLocations] =
    useState<DrayageSearchShippingLocations>({
      terminalGroups: [],
      terminals: [],
      depots: [],
      pricingTiers: [],
      locationGroups: [],
    });

  const handleProfilesOptions = useCallback(
    (profiles: Array<{ id: number; name: string }>): Option[] => {
      return profiles.map((profile) => ({
        value: profile.id,
        label: profile.name,
      }));
    },
    [],
  );

  const handleFscOptionsName = useCallback(
    (profilesOptions: Option[]): Option[] => {
      return profilesOptions.map((option) => {
        const pricingProfile = profiles.pricing.find(
          (profile) => profile.id === calculationData.tariffPricingTierId,
        );

        if (pricingProfile?.fuelSurchargeProfile.id === option.value) {
          return {
            ...option,
            label: `${pricingProfile.fuelSurchargeProfile.name} (From ${pricingProfile.name})`,
          };
        }
        return option;
      });
    },
    [profiles.pricing, calculationData.tariffPricingTierId],
  );

  const handleOeOptionsName = useCallback(
    (profilesOptions: Option[]): Option[] => {
      return profilesOptions.map((option) => {
        const pricingProfile = profiles.pricing.find(
          (profile) => profile.id === calculationData.tariffPricingTierId,
        );

        if (pricingProfile?.operationalExpensesProfile.id === option.value) {
          return {
            ...option,
            label: `${pricingProfile.operationalExpensesProfile.name} (From ${pricingProfile.name})`,
          };
        }
        return option;
      });
    },
    [profiles.pricing, calculationData.tariffPricingTierId],
  );

  const profileOptions = useMemo(
    () => ({
      customer: handleProfilesOptions(profiles.customer),
      pricing: handleProfilesOptions(profiles.pricing),
      fsc: handleFscOptionsName(handleProfilesOptions(profiles.fsc)),
      oe: handleOeOptionsName(handleProfilesOptions(profiles.oe)),
    }),
    [
      profiles,
      handleProfilesOptions,
      handleFscOptionsName,
      handleOeOptionsName,
    ],
  );

  const getLocation = useMemo(
    () => (isPickup: boolean) => {
      const field = isPickup
        ? "containerPickupLocation"
        : "containerDeliveryLocation";

      const containerLocation = calculationData[field];

      if (containerLocation.terminalGroupId) {
        const originOption = shippingLocations.terminalGroups?.find(
          (group) => group.id === containerLocation.terminalGroupId,
        );
        return `terminalGroups-${originOption?.id}`;
      }

      if (containerLocation.terminalId) {
        const originOption = shippingLocations.terminals.find(
          (group) => group.id === containerLocation.terminalId,
        );
        return `terminals-${originOption?.id}`;
      }

      if (containerLocation.depotId) {
        const originOption = shippingLocations.depots.find(
          (group) => group.id === containerLocation.depotId,
        );
        return `depots-${originOption?.id}`;
      }

      if (containerLocation.tariffPricingTierId) {
        const originOption = shippingLocations.pricingTiers.find(
          (group) => group.id === containerLocation.tariffPricingTierId,
        );
        return `pricingTiers-${originOption?.id}`;
      }

      if (containerLocation.locationGroupId) {
        const originOption = shippingLocations.locationGroups.find(
          (group) => group.id === containerLocation.locationGroupId,
        );
        return `locationGroups-${originOption?.id}`;
      }

      return undefined;
    },
    [calculationData, shippingLocations],
  );

  const originLocationValue = getLocation(true);

  const shippingLocationsOptions = useMemo(() => {
    const options: Option<DrayageShippingLocationsProperties>[] = [];
    const { terminals, depots, pricingTiers, locationGroups } =
      shippingLocations;

    const addGroupToOptions = (
      items: any[],
      groupName: string,
      groupLabel: string,
    ) => {
      if (items?.length) {
        options.push({
          label: groupLabel,
          value: groupName,
          disabled: true,
        });

        items.forEach((item) => {
          options.push({
            value: `${groupName}-${item.id}`,
            label: item.name,
            properties: {
              ...item,
              groupName,
            },
          });
        });
      }
    };

    addGroupToOptions(pricingTiers, "pricingTiers", "Pricing Tiers");
    addGroupToOptions(locationGroups, "locationGroups", "Location Groups");
    addGroupToOptions(terminals, "terminals", "Terminals");
    addGroupToOptions(depots, "depots", "Depots");

    return options;
  }, [shippingLocations]);

  const fetchProfiles = useCallback(async () => {
    try {
      setUiState((prev) => ({ ...prev, loading: LoadingTypesEnum.pending }));

      const [pricing, fsc, oe] = await Promise.all([
        drayageApi.getTariffPricingTiersLimited(initialTPTParams),
        drayageApi.getFuelSurchargeProfiles(initialFSCParams),
        drayageApi.getOperationalExpensesProfiles(initialOEParams),
      ]);

      if (pricing.hasError || fsc.hasError || oe.hasError) {
        const errorsCombined = [pricing, fsc, oe].reduce((acc, curr) => {
          if (curr.hasError) {
            acc.push(getApiErrorForDisplay(curr.errors));
          }
          return acc;
        }, [] as string[]);

        const errorMessage = errorsCombined.join(". ");

        addNotification({
          type: "error",
          message: errorMessage,
        });
      }

      setProfiles((prev) => ({
        ...prev,
        pricing: pricing.tariffPricingTiers,
        fsc: fsc.fuelSurchargeProfiles,
        oe: oe.operationalExpensesProfiles,
      }));

      setUiState((prev) => ({ ...prev, loading: LoadingTypesEnum.fulfilled }));
    } catch (error) {
      setUiState((prev) => ({ ...prev, loading: LoadingTypesEnum.rejected }));

      addNotification({ type: "error", message: error });
    }
  }, []);

  const onFetchCustomerProfiles = useCallback(async (searchQuery: string) => {
    try {
      const data = await customerApi.getCustomerProfilesSuggestions({
        searchQuery,
      });

      if (data.hasError) {
        addNotification({
          type: "error",
          message: getApiErrorForDisplay(data.errors),
        });
      }

      setProfiles((prev) => ({
        ...prev,
        customer: data.customerProfileSuggestions,
      }));
    } catch (error) {
      addNotification({ type: "error", message: error });
    }
  }, []);

  const onSearchPickupLocations = useCallback(async (query: string) => {
    try {
      const data = await drayageApi.getDrayageSearchShippingLocations({
        query,
      });

      if (data.hasError) {
        addNotification({
          type: "error",
          message: getApiErrorForDisplay(data.errors),
        });
      }

      if (data) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { errors, hasError, ...actualData } = data;
        setShippingLocations(actualData);
      }
    } catch (error) {
      addNotification({ type: "error", message: error });
    }
  }, []);

  const onLocationChange = useCallback(
    (location: LocationParams, isPickup: boolean) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { coords, ...rest } = location;
      const locationData: DrayageLocation = {
        terminalGroupId: null,
        terminalId: null,
        depotId: null,
        locationGroupId: null,
        tariffPricingTierId: null,
        locationAddress: {
          zipCode: rest.zipCode ?? null,
          city: rest.city ?? null,
          state: rest.state ?? null,
          address: rest.address ?? null,
        },
      };

      setCalculationData((prev) => ({
        ...prev,
        [isPickup ? "containerPickupLocation" : "containerDeliveryLocation"]:
          locationData,
      }));
    },
    [],
  );

  const onTerminalLocationChange = useCallback(
    (option: Option<DrayageShippingLocationsProperties>, isPickup: boolean) => {
      if (!option.properties) return;

      const field = isPickup
        ? "containerPickupLocation"
        : "containerDeliveryLocation";

      const baseLocation = {
        terminalGroupId: null,
        terminalId: null,
        depotId: null,
        locationGroupId: null,
        tariffPricingTierId: null,
        locationAddress: null,
      };

      const locationUpdates = {
        terminalGroups: { terminalGroupId: option.properties.id },
        terminals: { terminalId: option.properties.id },
        depots: { depotId: option.properties.id },
        pricingTiers: { tariffPricingTierId: option.properties.id },
        locationGroups: { locationGroupId: option.properties.id },
      };

      setCalculationData((prev) => ({
        ...prev,
        [field]: {
          ...baseLocation,
          ...(locationUpdates[option.properties.groupName] || {}),
        },
      }));
    },
    [],
  );

  const onToggleDeliveryType = useCallback(() => {
    setCalculationData((prev) => ({
      ...prev,
      containerPickupLocation: initialCalculationData.containerPickupLocation,
    }));
    setIsTerminalPickup((prev) => !prev);
  }, []);

  const isSearchEnabled = () => {
    const {
      tariffPricingTierId,
      fuelSurchargeProfileId,
      operationalExpensesProfileId,
      containerSize,
      containerPickupLocation,
      containerDeliveryLocation,
    } = calculationData;

    const isLocationFilledSet = (location: typeof containerPickupLocation) =>
      location.terminalGroupId ||
      location.terminalId ||
      location.depotId ||
      location.tariffPricingTierId ||
      location.locationGroupId ||
      location.locationAddress;

    return (
      tariffPricingTierId &&
      fuelSurchargeProfileId &&
      operationalExpensesProfileId &&
      containerSize &&
      isLocationFilledSet(containerPickupLocation) &&
      isLocationFilledSet(containerDeliveryLocation)
    );
  };

  const getProfileFromPricingTier = useCallback(
    (pricingTierId: number, profileType: "fsc" | "oe"): number => {
      const pricingTier = profiles.pricing.find(
        (profile) => profile.id === pricingTierId,
      );
      if (!pricingTier) return 0;

      return profileType === "fsc"
        ? pricingTier.fuelSurchargeProfile.id
        : pricingTier.operationalExpensesProfile.id;
    },
    [profiles.pricing],
  );

  const onQuoteSelect = useCallback((quote?: DrayageQuote) => {
    setCalculationState((prev) => ({ ...prev, selectedQuote: quote }));
  }, []);

  const onOpenRouteDetailsCard = useCallback((quote: DrayageQuote) => {
    setCalculationState((prev) => ({ ...prev, selectedQuote: quote }));
    setUiState((prev) => ({ ...prev, isRouteDetailsCardOpen: true }));
  }, []);

  const onGetRouteEstimates = useCallback(async () => {
    try {
      setUiState((prev) => ({
        ...prev,
        loading: LoadingTypesEnum.pending,
      }));

      if (uiState.accordionOpenItems.length === 0) {
        setUiState((prev) => ({ ...prev, accordionOpenItems: ["map"] }));
      }

      onQuoteSelect(undefined);

      const data = await drayageApi.calculateDrayageQuote(calculationData);

      if (data.hasError) {
        addNotification({
          type: "error",
          message: getApiErrorForDisplay(data.errors),
        });
      }

      if (data) {
        setCalculationState((prev) => ({
          ...prev,
          response: data,
          selectedQuote: data.quotes?.[0],
        }));
      }

      setUiState((prev) => ({
        ...prev,
        loading: LoadingTypesEnum.fulfilled,
      }));
    } catch (error) {
      setUiState((prev) => ({ ...prev, loading: LoadingTypesEnum.rejected }));

      addNotification({ type: "error", message: error });
    }
  }, [calculationData]);

  const onAddNewCustomerProfile = useCallback(
    async (data: BaseCustomerProfileParams) => {
      try {
        setUiState((prev) => ({
          ...prev,
          newCustomerProfileLoading: LoadingTypesEnum.pending,
        }));

        const newProfile = await customerApi.addCustomerProfile(data);

        setCalculationData((prev) => ({
          ...prev,
          companyId: newProfile.id,
        }));

        setUiState((prev) => ({
          ...prev,
          newCustomerProfileLoading: LoadingTypesEnum.fulfilled,
        }));

        await onFetchCustomerProfiles("");
      } catch (error) {
        setUiState((prev) => ({
          ...prev,
          newCustomerProfileLoading: LoadingTypesEnum.rejected,
        }));

        addNotification({ type: "error", message: error });
      }
    },
    [onFetchCustomerProfiles],
  );

  const onCustomerProfileFormSubmit = useCallback(
    async (data: BaseCustomerProfileParams) => {
      await onAddNewCustomerProfile(data);
      setUiState((prev) => ({ ...prev, isAddCustomerProfileFormOpen: false }));
    },
    [onAddNewCustomerProfile],
  );

  const onFetchTolls = useCallback(async (quoteRequestId: number) => {
    try {
      setUiState((prev) => ({
        ...prev,
        tollsLoading: LoadingTypesEnum.pending,
      }));

      const tollsData = await drayageApi.calculateRouteTolls({
        quoteRequestId,
      });

      setCalculationState((prev) => ({ ...prev, allTolls: tollsData }));

      setUiState((prev) => ({
        ...prev,
        tollsLoading: LoadingTypesEnum.fulfilled,
      }));
    } catch (error) {
      setUiState((prev) => ({
        ...prev,
        tollsLoading: LoadingTypesEnum.rejected,
      }));
    }
  }, []);

  const setOfferPrice = async (
    id: number,
    offeredPrice: number,
    isOfferAccepted: boolean,
  ) => {
    try {
      await drayageApi.setOfferPrice({
        id,
        offeredPrice,
        isOfferAccepted,
      });
    } catch (error) {
      addNotification({ type: "error", message: error });
    }
  };

  const getBarriersData = useCallback((): TollBarriersData[] | undefined => {
    const quotesTolls = calculationState.allTolls?.tolls?.find(
      (tollsEntry) =>
        tollsEntry.quoteResultId ===
        calculationState.selectedQuote?.quoteResultId,
    );

    if (!quotesTolls?.goingRouteTolls && !quotesTolls?.returnRouteTolls)
      return undefined;

    const tolls = [
      ...(quotesTolls.goingRouteTolls || []),
      ...(quotesTolls.returnRouteTolls || []),
    ];
    if (!tolls.length) return undefined;

    return tolls
      .map(({ cashCost, barrierSystemLocation }) => {
        if (!barrierSystemLocation) return undefined;
        const { longitude, latitude } = barrierSystemLocation;
        return {
          price: cashCost,
          coordinates: [longitude, latitude],
        };
      })
      .filter((data): data is TollBarriersData => !!data?.coordinates);
  }, [calculationState.allTolls, calculationState.selectedQuote]);

  useEffect(() => {
    const initializeData = async () => {
      await Promise.all([
        onFetchCustomerProfiles(""),
        fetchProfiles(),
        onSearchPickupLocations(""),
      ]);
    };

    initializeData();
  }, []);

  useEffect(() => {
    if (calculationState.response?.drayageQuoteRequestId) {
      onFetchTolls(calculationState.response.drayageQuoteRequestId);
    }
  }, [calculationState.response?.drayageQuoteRequestId, onFetchTolls]);

  useEffect(() => {
    if (calculationData.tariffPricingTierId === 0 && profiles.pricing.length) {
      const tariffPricingTierId =
        defaultDrayageTariffPricingTier?.id ?? profiles.pricing[0].id;

      setCalculationData((prev) => ({
        ...prev,
        tariffPricingTierId,
        fuelSurchargeProfileId: getProfileFromPricingTier(
          tariffPricingTierId,
          "fsc",
        ),
        operationalExpensesProfileId: getProfileFromPricingTier(
          tariffPricingTierId,
          "oe",
        ),
      }));
    }
  }, [
    profiles.pricing,
    defaultDrayageTariffPricingTier,
    getProfileFromPricingTier,
  ]);

  return {
    isTerminalPickup,
    loading: uiState.loading,
    newCustomerProfileLoading:
      uiState.newCustomerProfileLoading === LoadingTypesEnum.pending,
    tollsLoading: uiState.tollsLoading,
    accordionOpenItems: uiState.accordionOpenItems,
    isAddCustomerProfileFormOpen: uiState.isAddCustomerProfileFormOpen,
    isRouteDetailsCardOpen: uiState.isRouteDetailsCardOpen,
    isPrintModalOpen: uiState.isPrintModalOpen,
    companyProfileOptions: profileOptions.customer,
    pricingTierOptions: profileOptions.pricing,
    fscOptions: profileOptions.fsc,
    oeOptions: profileOptions.oe,
    terminalPickupLocationValue: originLocationValue,
    shippingLocationsOptions,
    initialCalculationData,
    calculationData,
    quotes: calculationState.response?.quotes,
    selectedQuote: calculationState.selectedQuote,
    companyDetails,
    tollBarriersData: getBarriersData(),
    routeOrgSearchFieldRef,
    routeDestSearchFieldRef,
    leftContentRef,
    routeCardsListRef,
    isSearchEnabled: isSearchEnabled(),
    onToggleDeliveryType,
    onTerminalLocationChange,
    onLocationChange,
    onGetRouteEstimates,
    onFetchCustomerProfiles,
    onSearchPickupLocations,
    onQuoteSelect,
    onOpenRouteDetailsCard,
    onCustomerProfileFormSubmit,
    setCalculationData,
    setUiState: (updates: Partial<typeof uiState>) =>
      setUiState((prev) => ({ ...prev, ...updates })),
    getFscFromPricingTier: (id: number) => getProfileFromPricingTier(id, "fsc"),
    getOeFromPricingTier: (id: number) => getProfileFromPricingTier(id, "oe"),
    getQuotesTolls: () =>
      calculationState.allTolls?.tolls?.find(
        (tollsEntry) =>
          tollsEntry.quoteResultId ===
          calculationState.selectedQuote?.quoteResultId,
      ),
    setOfferPrice,
  };
};

export default useDrayageCalculator;
